import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Google OAuth Callback - TALENT ONLY
 * 
 * In the simplified auth model:
 * - Google Login is EXCLUSIVELY for Talent role
 * - No role selection after Google auth
 * - Trainer and Enterprise must use Email/Password
 */
export default function GoogleCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");
  const [error, setError] = useState(null);

  useEffect(() => {
    processGoogleAuth();
  }, []);

  const processGoogleAuth = async () => {
    const session_id = searchParams.get("session_id");
    
    if (!session_id) {
      setStatus("error");
      setError("Authentication failed. Please try again.");
      setTimeout(() => navigate("/"), 3000);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/google/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle case where email is already registered as Trainer/Enterprise
        if (response.status === 400 && data.detail?.includes("registered as")) {
          setStatus("error");
          setError(data.detail);
          toast.error(data.detail, { duration: 5000 });
          setTimeout(() => navigate("/login", { state: { role: "trainer" } }), 3000);
          return;
        }
        throw new Error(data.detail || "Authentication failed");
      }

      localStorage.setItem("token", data.session_token);
      localStorage.setItem("user", JSON.stringify(data.user));

      toast.success("Welcome to TalentMeet!");
      setStatus("success");
      
      // Navigate to dashboard
      navigate("/talent/dashboard");
    } catch (error) {
      console.error("Auth error:", error);
      setStatus("error");
      setError(error.message || "Authentication failed. Please try again.");
      toast.error(error.message || "Authentication failed");
      setTimeout(() => navigate("/"), 3000);
    }
  };

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-6">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">Redirecting you back...</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-600">Welcome! Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-violet-600" />
        <p className="mt-4 text-gray-600">Completing sign in...</p>
        <p className="mt-2 text-sm text-gray-400">Setting up your Talent account</p>
      </div>
    </div>
  );
}
